/**
 * Pure formatter helpers for blog UI. Lives in a separate file from
 * `src/lib/blog.ts` because that module imports `node:fs` for the
 * markdown loader and would taint any client component that touched it.
 *
 * The formatter table here is hand-written rather than going through
 * `Intl.DateTimeFormat` because Azerbaijani month names are
 * unreliable across Node ICU builds: some snapshots collapse the
 * long form to a single letter ("M" for "May"), some return the short
 * form regardless of the option, and the short form is itself one
 * letter on certain V8 builds. Hand-mapping eliminates the
 * variability and gives identical output across build / runtime
 * environments and across the three locales.
 *
 *   az: lowercase native spelling - "04 may 2026"
 *   ru: genitive case (date convention) - "04 мая 2026"
 *   en: standard month names - "04 May 2026"
 */
import type { Locale } from "@/lib/i18n-config";

const MONTHS_LONG: Record<Locale, readonly string[]> = {
  az: ["yanvar", "fevral", "mart", "aprel", "may", "iyun",
       "iyul", "avqust", "sentyabr", "oktyabr", "noyabr", "dekabr"],
  ru: ["января", "февраля", "марта", "апреля", "мая", "июня",
       "июля", "августа", "сентября", "октября", "ноября", "декабря"],
  en: ["January", "February", "March", "April", "May", "June",
       "July", "August", "September", "October", "November", "December"],
};

// Month names on their own (a list's month heading): the nominative,
// capitalised; the dates above take the genitive ("4 iyul", "4 июля").
const MONTHS_NOM: Record<Locale, readonly string[]> = {
  az: ["Yanvar", "Fevral", "Mart", "Aprel", "May", "İyun",
       "İyul", "Avqust", "Sentyabr", "Oktyabr", "Noyabr", "Dekabr"],
  ru: ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
       "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"],
  en: MONTHS_LONG.en,
};

/** A month heading: "Sentyabr", or "Sentyabr 2025" outside the current
 *  year (the same rule as formatMonthDay). `month` is 0-based. */
export function formatMonthYear(year: number, month: number, locale: Locale): string {
  const name = MONTHS_NOM[locale][month];
  return year === new Date().getFullYear() ? name : `${name} ${year}`;
}

/** Format an ISO date (YYYY-MM-DD) as "DD <month> YYYY". */
export function formatPublishedAt(iso: string, locale: Locale): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const year = m[1];
  const month = parseInt(m[2], 10);
  const day = m[3];
  if (month < 1 || month > 12) return iso;
  return `${day} ${MONTHS_LONG[locale][month - 1]} ${year}`;
}

/** Format a millisecond timestamp as "D <month> YYYY" using the long
 *  month name. Year omitted when it matches the current year so the
 *  same-year case stays compact ("4 May" vs "4 May 2025"). Reuses the
 *  hand-mapped MONTHS_LONG table to dodge the same Node-ICU
 *  collapsing-to-"M" bug that bit the blog formatter. */
export function formatMonthDay(ts: number, locale: Locale): string {
  const d = new Date(ts);
  const day = d.getDate();
  const month = MONTHS_LONG[locale][d.getMonth()];
  const year = d.getFullYear();
  const now = new Date().getFullYear();
  return year === now ? `${day} ${month}` : `${day} ${month} ${year}`;
}

// Short month names, hand-mapped for the same reason as MONTHS_LONG.
// Used for compact chart axis ticks where the long form would overlap.
const MONTHS_SHORT: Record<Locale, readonly string[]> = {
  az: ["yan", "fev", "mar", "apr", "may", "iyn",
       "iyl", "avq", "sen", "okt", "noy", "dek"],
  ru: ["янв", "фев", "мар", "апр", "май", "июн",
       "июл", "авг", "сен", "окт", "ноя", "дек"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
       "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

/** "4 yan" - localized day + short month for compact chart axis ticks.
 *  UTC-based to match ApexCharts `datetimeUTC` + the ISO-week timestamps
 *  the charts plot, so the tick label names the same day the axis marks.
 *  Replaces ApexCharts' built-in "d MMM" format, which is English-only. */
export function formatMonthDayShort(ts: number, locale: Locale): string {
  const d = new Date(ts);
  return `${d.getUTCDate()} ${MONTHS_SHORT[locale][d.getUTCMonth()]}`;
}

/** "4 yanvar 2026" - full localized date for chart tooltips (UTC), the
 *  localized replacement for ApexCharts' English "dd MMM yyyy". */
export function formatDateLong(ts: number, locale: Locale): string {
  const d = new Date(ts);
  return `${d.getUTCDate()} ${MONTHS_LONG[locale][d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Localized human label for the post category enum. */
export type BlogCategory = "news" | "guides" | "market";

const CATEGORY_LABELS: Record<BlogCategory, Record<Locale, string>> = {
  news: { az: "Yeniliklər", ru: "Новости", en: "News" },
  // "guides" = how-to articles. Earlier translations used the
  // "tour guide" sense in both languages: "Bələdçilər" reads as
  // people who guide, and "Гиды" is specifically tour guides too.
  // Replaced with the natural blog-category words: az "Təlimatlar"
  // (instructions / guides, the standard term in AZ tech blogs)
  // and ru "Гайды" (modern web parlance, what tech-blog readers
  // actually see — "Руководства" is the formal alternative but
  // reads as printed manuals).
  guides: { az: "Təlimatlar", ru: "Гайды", en: "Guides" },
  market: { az: "Bazar", ru: "Рынок", en: "Market" },
};

export function categoryLabel(category: BlogCategory, locale: Locale): string {
  return CATEGORY_LABELS[category][locale];
}
